import {
  ADVERT_FORMAT_TYPES,
  ADVERT_ITINERARY_SEARCH_TYPES,
  DISABLE_PRICE_PARTNER_VALUE,
  FLIGHTS_INLINE_TYPES,
  SERVICE,
} from '@web-platform/shared-universal-constants/src/adverts';
import { generateAdvertIdFromStrevdaSlot } from '@web-platform/shared-universal-utils-frontend/src/helpers/generateAdvertId';

import type { FlightsInlineFields } from '@skyscanner/strevda-gam';
import type { Slot } from '@web-platform/shared-universal-types/src/network/endpoints/strevda';
import type {
  CombinedResultsFlightsInlineAdvert,
  AnalyticsProperties,
  CreativeFlightsInline,
  CreativeFlightsPricedBrandInline,
  CreativeFlightsUnpricedBrandInline,
  CreativeFlightsInlineItinerary,
  Selection,
  Format,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/combinedResultsFlightsInline';

const getFlightsInlineCreativeFromSlotParams = (slot: Slot): CreativeFlightsInline => {
  const { advert, position } = slot;

  const {
    clickthroughUrl: redirectUrl,
    deeplink,
    layoutType,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    removedeeplinkids,
    search,
    title,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- inherited from banana, strevda types require assertion
  } = advert.fields as FlightsInlineFields;

  // Creatives from strevda-runtime (via DFP) will always be of type CreativeFlightsInline
  // Default values are set because DFP will not provide them
  const slotFields = {
    appIndex: position.toString(),
    callToActionText: null,
    ctaButtonTitleEnabled: true,
    deeplink,
    deeplinkParams: null,
    itineraryId: null,
    message,
    partner,
    partnerDesktopLogo: partnerDesktopLogo || null,
    partnerLogo: partnerLogo || null,
    // DFP ads are out of DSA scope — pioPartnerName not provided by DFP
    pioPartnerName: '',
    redirectUrl,
    removeDeeplinkIds: (removedeeplinkids || '').toLowerCase() === 'true',
    requestedDepth: null,
    title,
  };

  // force-map legacy ads to Unpriced Brand Inline
  if (partner === DISABLE_PRICE_PARTNER_VALUE) {
    const creative: CreativeFlightsUnpricedBrandInline = {
      appIndex: slotFields.appIndex,
      callToActionText: slotFields.callToActionText,
      ctaButtonTitleEnabled: slotFields.ctaButtonTitleEnabled,
      format: ADVERT_FORMAT_TYPES.UNPRICED_BRAND_INLINE,
      message: slotFields.message,
      partnerDesktopLogo: slotFields.partnerDesktopLogo,
      partnerLogo: slotFields.partnerLogo,
      partner: null,
      pioPartnerName: slotFields.pioPartnerName,
      redirectUrl: slotFields.redirectUrl,
      title: slotFields.title,
    };

    return creative;
  }

  if (layoutType === FLIGHTS_INLINE_TYPES.BRAND) {
    if (search === ADVERT_ITINERARY_SEARCH_TYPES.UNPRICED) {
      const creative: CreativeFlightsUnpricedBrandInline = {
        appIndex: slotFields.appIndex,
        callToActionText: slotFields.callToActionText,
        ctaButtonTitleEnabled: slotFields.ctaButtonTitleEnabled,
        format: ADVERT_FORMAT_TYPES.UNPRICED_BRAND_INLINE,
        message: slotFields.message,
        partnerDesktopLogo: slotFields.partnerDesktopLogo,
        partnerLogo: slotFields.partnerLogo,
        partner: slotFields.partner,
        pioPartnerName: slotFields.pioPartnerName,
        redirectUrl: slotFields.redirectUrl,
        title: slotFields.title,
      };

      return creative;
    }

    const creative: CreativeFlightsPricedBrandInline = {
      ...slotFields,
      format: ADVERT_FORMAT_TYPES.PRICED_BRAND_INLINE,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- inherited from banana, strevda search type mismatch
      selection: search as unknown as Selection,
    };

    return creative;
  }

  const creative: CreativeFlightsInlineItinerary = {
    ...slotFields,
    format: ADVERT_FORMAT_TYPES.INLINE,
    layoutType: FLIGHTS_INLINE_TYPES.ITINERARY,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- inherited from banana, strevda search type mismatch
    selection: search as unknown as Selection,
  };

  return creative;
};

export default function transformStrevdaSlotToAdvert(
  slot: Slot,
): CombinedResultsFlightsInlineAdvert {
  const { advert, position } = slot;
  const { trackingContext, trackingPixels } = advert;
  const creative: CreativeFlightsInline = getFlightsInlineCreativeFromSlotParams(slot);
  const analyticsProperties: AnalyticsProperties = {
    idCampaign: trackingContext.idCampaign,
    idCreative: trackingContext.idCreative,
    idPlacement: trackingContext.idPlacement,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- inherited from banana, tracking context type mismatch
    idFormat: trackingContext.idFormat as Format,
    idSponsor: trackingContext.idSponsor,
    idTracking: trackingContext.idTracking,
    idResponse: trackingContext.idResponse,
    market: trackingContext.market,
    priority: 'UNSET_PRIORITY_TYPE',
    service: SERVICE.DFP,
  };

  const id: string = generateAdvertIdFromStrevdaSlot({
    advert,
    position,
  });

  return {
    id,
    position,
    creative,
    analyticsProperties,
    pixels: trackingPixels,
  };
}
